import { useEffect, useRef, useState } from 'react';

interface ScreenTransitionProps {
  screenKey: string;
  children: React.ReactNode;
}

export function ScreenTransition({ screenKey, children }: ScreenTransitionProps) {
  const [currentKey, setCurrentKey] = useState(screenKey);
  const [currentChildren, setCurrentChildren] = useState(children);
  const [exitingChildren, setExitingChildren] = useState<React.ReactNode>(null);
  const prevKeyRef = useRef(screenKey);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (screenKey === prevKeyRef.current) {
      // Same screen — update children in place
      setCurrentChildren(children);
      return;
    }

    // Screen changed — start transition
    // Cancel any in-progress transition
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }

    // Snapshot current children as exiting
    setExitingChildren(currentChildren);

    // Update to new screen
    setCurrentKey(screenKey);
    setCurrentChildren(children);
    prevKeyRef.current = screenKey;

    // Remove exiting screen after animation
    timerRef.current = setTimeout(() => {
      setExitingChildren(null);
      timerRef.current = undefined;
    }, 300);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [screenKey, children]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column' }}>
      {/* Exiting screen */}
      {exitingChildren && (
        <div className="screen-exit" style={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          pointerEvents: 'none',
        }}>
          {exitingChildren}
        </div>
      )}
      {/* Current screen */}
      <div key={currentKey} className={exitingChildren ? 'screen-enter' : ''} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {currentChildren}
      </div>
    </div>
  );
}
